import { createStyles, useMantineTheme, ActionIcon, Box } from '@mantine/core';
import React from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { match } from 'ts-pattern';
import { useMediaQuery } from 'react-responsive';

import { IconChevronLeft } from '~/domains/design-system/icons/IconChevronLeft';
import { ButtonLink } from '~/domains/design-system/Button';
import { Routes } from '~/domains/common/constants/routes';
import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';
import { BRANDS } from '~/domains/auth/consts';

const useStyles = createStyles((theme) => ({
  stepsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    minHeight: 28,
    marginBottom: /* eslint-disable-line local-rules/use-theme-tokens */ 20,
  },

  backButton: {
    marginLeft: -theme.other.spacing._12,
    [theme.fn.largerThan('md')]: {
      position: 'absolute',
      left: -44,
    },
    [theme.fn.largerThan(1100)]: {
      left: -66,
    },
  },

  buttonLink: {
    color: theme.colors.gray2[3],
    minWidth: 47,
  },

  skipButton: {
    padding: /* eslint-disable-line local-rules/use-theme-tokens */ '0px 18px',
    fontSize: '16px',
    lineHeight: '1.5',
    fontWeight: 900,
    minWidth: 47,
    color: theme.colors.gray2[3],
    cursor: 'pointer',

    '@media (hover: hover)': {
      transition: 'opacity 0.15s ease-in-out',
    },

    '&:hover': {
      textDecoration: 'underline',
      opacity: 0.5,
    },
  },

  stepIndicator: {
    marginLeft: 0,
  },
}));

interface HeaderProps {
  showBackButton?: boolean;
  prevStep?: () => void;
  rightButton?: React.ReactNode;
  dynamicText?: string;
  stepIndicatorText?: string;
}

export function Header({
  showBackButton = false,
  prevStep,
  rightButton,
  dynamicText,
  stepIndicatorText,
}: HeaderProps) {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery({ maxWidth: 500 });

  return (
    <div className={classes.stepsContainer}>
      {showBackButton && prevStep && (
        <ActionIcon onClick={prevStep} className={classes.backButton}>
          <IconChevronLeft size={32} color={theme.colors.gray[7]} />
        </ActionIcon>
      )}

      {(!showBackButton || !prevStep) && isMobile && <Box w={47} />}

      <div className={classes.stepIndicator}>
        <Text variant="body-large" c="gray2.3">
          {stepIndicatorText}
        </Text>
      </div>

      {rightButton || <div>{dynamicText}</div>}
    </div>
  );
}

interface StepsHeaderProps {
  currentStepIndex: number;
  steps: string[];
  prevStep: () => void;
  onSkipKYCClick: () => void;
  preAuthStepCount: number;
  brand?: BRANDS;
}

export function StepsHeader({
  currentStepIndex,
  steps,
  prevStep,
  onSkipKYCClick,
  preAuthStepCount,
  brand = BRANDS.SPLASH,
}: StepsHeaderProps) {
  const { t } = useTranslation('auth');
  const { classes } = useStyles();
  const router = useRouter();

  // A `requireKyc=1` flow (Contest Vault withdraw, EDGE-32343) sends the user here to
  // complete mandatory KYC, so the KYC step must not offer Skip. Any `1` wins, mirroring
  // the parsing in useIsShouldSkipKycAfterSignUp and user-verification.
  const { requireKyc } = router.query;
  const isKycRequired = Array.isArray(requireKyc) ? requireKyc.includes('1') : requireKyc === '1';

  const stepIndicatorText = t('signUp.steps.status', {
    current: steps[currentStepIndex],
    total: steps.length,
  });

  // @todo - create component for this
  const loginLink = (
    <ButtonLink
      variant="secondary-fill"
      className={classes.buttonLink}
      mode="linkButton"
      size="large"
      type="text"
      href={Routes.signIn({
        redirectTo: `${router.query.redirectTo}`,
        redirectToEO: `${router.query.redirectToEO}`,
        brand,
      })}
    >
      <Title variant="title-medium" c="gray2.6">
        {t('signUp.logIn')}
      </Title>
    </ButtonLink>
  );

  const skipLink = isKycRequired ? null : (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div className={classes.skipButton} onClick={onSkipKYCClick}>
      <Title variant="title-medium" c="gray2.6">
        {t('signUp.skip')}
      </Title>
    </div>
  );

  if (brand === 'RYP') {
    return (
      <div>
        {match(currentStepIndex)
          .with(0, () => <Header rightButton={loginLink} stepIndicatorText={stepIndicatorText} />)
          .with(1, () => (
            <Header showBackButton prevStep={prevStep} stepIndicatorText={stepIndicatorText} />
          ))
          .when(
            (i) => i > 1,
            () => <Header stepIndicatorText={stepIndicatorText} />
          )
          .otherwise(() => null)}
      </div>
    );
  }

  // Dynamic step index mapping:
  // 0 = Email, 1 = Password, 2..preAuthStepCount-1 = EmailVerification/Handle (post-register, no back, no skip)
  // preAuthStepCount+ = KYC steps (with skip button)
  const kycOffset = currentStepIndex - preAuthStepCount;

  return (
    <div>
      {match(currentStepIndex)
        .with(0, () => <Header rightButton={loginLink} stepIndicatorText={stepIndicatorText} />)
        .with(1, () => (
          <Header
            showBackButton
            prevStep={prevStep}
            rightButton={loginLink}
            stepIndicatorText={stepIndicatorText}
          />
        ))
        .when(
          (i) => i > 1 && i < preAuthStepCount,
          () => <Header stepIndicatorText={stepIndicatorText} />
        )
        .when(
          (i) => i >= preAuthStepCount,
          () => {
            // KYC steps: show skip link, show back button for most steps
            // No back for first KYC step (VerificationType/Name) and Name-after-Prove (offset 3)
            const showBack = kycOffset !== 0 && kycOffset !== 3;
            return (
              <Header
                showBackButton={showBack}
                rightButton={skipLink}
                prevStep={prevStep}
                stepIndicatorText={stepIndicatorText}
              />
            );
          }
        )
        .otherwise(() => null)}
    </div>
  );
}
